/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Box, Button, Stack } from "@mui/material";
import { useSortableComponentList } from "cms/components/hooks/use-sortable-component-list";
import { SVGIcons } from "cms/icons/svg-icons";
import { AddLineWithIcon } from "components/add-line-with-icon";
import LabelledField from "components/labelled-field";
import PositionButtons from "components/position-buttons";
import RemoveIcon from "components/remove-icon";
import { SortableContent } from "components/sortable-content";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { isArrayNotEmpty } from "utils/empty-exists";
import { ComponentCarouselContext } from "./component-carousel-context";

const defaultCarouselSlide = Object.freeze({
    title: "",
    description: {},
    image: {},
    link: {},
});

/**
 * @param {object} props
 * @param {FeaturedWebpage[]} props.slides
 * @param {(slides: HeroCarouselSlide[]) => void} props.onChangeList
 */
const CarouselComponentEditWrapper = (props) => {
    
    const { t } = useTranslation();
    const {
        slides: initialSlides = [],
        onChangeList,
        children,
    } = props;

    if (typeof onChangeList !== "function") {
        throw Error("onChangeList was not supplied, or is not a function");
    }

    const {
        list,
        sortableMethods,
        controls,
    } = useSortableComponentList( initialSlides, defaultCarouselSlide, onChangeList);

    const {
        addNewItem,
        addNewItemBelow,
        removeItem,
        updateItems
    } = controls;

    return (
        <Fragment>
            <LabelledField
                label={t("commons.carousel")}
                style={{ marginTop: 12 }}
            >
                <Box style={{ paddingTop: 6, display: "flex", gap: 12 }}>
                    <Button
                        size='medium'
                        variant='contained'
                        color={"primary"}
                        target='warning'
                        onClick={addNewItem}
                    >
                        <SVGIcons.common.Create style={{ paddingRight: 4, cursor: "pointer" }} />
                        {t("commons.addCarouselSlide")}
                    </Button>
                </Box>
            </LabelledField>
            <div className='simplicity-carousel-wrapper'>
                <Stack direction='column'>
                    {isArrayNotEmpty(list) && list.map((item, index) => {
                        const { guid } = item;
                        const componentCarouselContext = {
                            list,
                            guid,
                            item,
                            index,
                            position: index,
                            addNewItem,
                            updateItems,
                            removeItem,
                        };
                        return (
                            <ComponentCarouselContext.Provider
                                key={`carousel-${guid}`}
                                value={componentCarouselContext}
                            >
                                <SortableContent
                                    id={guid}
                                    index={index}
                                    sortableMethods={sortableMethods}
                                >
                                    
                                    {children}
                                    
                                    <div>
                                        <PositionButtons
                                            index={index}
                                            list={list}
                                            handleUpdate={updateItems}
                                        />
                                        <RemoveIcon
                                            className='simplicity-composer-field-editor-remove'
                                            onDeleteItem={() => removeItem(index)}
                                            index={index}
                                        />
                                    </div>
                                </SortableContent>
                                <AddLineWithIcon
                                    addNewItem={() => addNewItemBelow(index)}
                                />
                            </ComponentCarouselContext.Provider>
                        );
                    })}
                </Stack>
            </div>
        </Fragment>
    );
};
CarouselComponentEditWrapper.displayName = "CarouselComponentEditWrapper";
export { CarouselComponentEditWrapper };


/**
 * @typedef {object} HeroCarouselSlide
 * @property {string} guid
 * @property {number} position
 * @property {string} title
 * @property {string} description
 * @property {string} image
 * @property {string} link
 */
